import { useMemo, useState } from "react";

import CreatableSelect from "react-select/creatable";
import CustomTooltip from "../../ui-components/tooltip";
import { Label } from "reactstrap";
import PropTypes from "prop-types";
import { REACT_STYLES } from "@src/utility/constants";
import classnames from "classnames";
import { selectThemeColors } from "@helpers/utils";
import size from "lodash/size";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

const createOption = (label) => ({
    label,
    value      : label,
    isCreatable: true
});

const CreatableInput = ({
    control,
    className = "",
    placeholder = "",
    label = "",
    errors,
    name,
    errMsg = "",
    required = false,
    disabled = false,
    id,
    showErrMsg = true,
    tooltipContent = "",
    options = [],
    maxAllowedSelection = null,
    isMulti = true
}) => {
    const { t } = useTranslation();

    const {
        field: { onChange, value }
    } = useController({
        name,
        control
    });

    const [inputValue, setInputValue] = useState("");
    const [showMaxSelectedWarning, setShowMaxSelectedWarning] = useState(false);

    const maxSelectedAllowed = maxAllowedSelection ?? 10;
    const selectedOptions = Array.isArray(value) ? value : value ? [value] : [];
    const existingLabels = useMemo(() => selectedOptions.map(v => v.label), [selectedOptions]);

    const handleInputChange = (val = "") => {
        setInputValue(val.trim());
    };

    const handleValues = () => {
        const trimmedInput = inputValue.trim();
        if (!trimmedInput || existingLabels.includes(trimmedInput)) return;

        if (isMulti) {
            if (selectedOptions.find(option => option.value === trimmedInput)) return;
            if (selectedOptions.length >= maxSelectedAllowed) {
                setShowMaxSelectedWarning(true);
                return;
            }

            let newOptions = [];
            if (trimmedInput.includes(",")) {
                const values = trimmedInput.split(",").map(v => v.trim()).filter(Boolean);
                newOptions = values
                    .filter(v => !existingLabels.includes(v))
                    .map(createOption);
            } else {
                newOptions = [createOption(trimmedInput)];
            }

            onChange([...selectedOptions, ...newOptions]);
        } else {
            onChange(createOption(trimmedInput));
        }

        setInputValue("");
        setShowMaxSelectedWarning(false);
    };

    const handleKeyDown = (event) => {
        if (!inputValue) return;
        if (["Enter", "Tab", ","].includes(event.key)) {
            handleValues();
            event.preventDefault();
        }
    };

    const handleBlur = () => {
        handleValues();
    };

    const handleOnChange = (val) => {
        if (isMulti) {
            if (val.length >= maxSelectedAllowed) {
                setShowMaxSelectedWarning(true);
                return;
            }
            setShowMaxSelectedWarning(false);
        }
        onChange(val);
    };

    const message = (errors && (errors[name]?.message || errors[id]?.message)) || errMsg;
    const isInvalid = !!(errors && (errors[name] || errors[id]));

    return (
        <div
            className={classnames({ [className]: className, "mb-1": !className })}
            id={`error-${name.replaceAll(".", "-")}`}>
            {label && (
                <Label htmlFor={name} data-cy={`label-${id}`}>
                    {t(label)}
                    {required && <span className="text-danger fw-bolder">*</span>}
                    {tooltipContent && (
                        <>
                            <i className="mdi mdi-information-outline ms-75" id={`tooltip-select-${id}`} />
                            <CustomTooltip target={`tooltip-select-${id}`} content={tooltipContent} />
                        </>
                    )}
                </Label>
            )}
            <CreatableSelect
                id={`create-${isMulti ? "multi" : "single"}-${id}`}
                components={!size(options) ? { DropdownIndicator: null } : undefined}
                inputValue={inputValue}
                isClearable
                isMulti={isMulti}
                escapeClearsValue={false}
                styles={REACT_STYLES({
                    type: isInvalid ? "error" : showMaxSelectedWarning ? "warning" : ""
                })}
                options={options}
                theme={selectThemeColors}
                className="react-select"
                classNamePrefix="select"
                placeholder={placeholder ? t(placeholder) : `${t("Enter")} ${t(label)}`}
                isDisabled={disabled}
                onBlur={handleBlur}
                onChange={handleOnChange}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                value={value || (isMulti ? [] : null)}
                menuIsOpen={!size(options) ? false : undefined}/>
            {!showErrMsg && message && (
                <CustomTooltip target={`error-${name.replaceAll(".", "-")}`} content={message} />
            )}
            {showMaxSelectedWarning && (
                <p>
                    <small className="text-warning">
                        {t("Max {{maxSelectedAllowed}} allowed", { maxSelectedAllowed })}
                    </small>
                </p>
            )}
            {showErrMsg && message && (
                <small className="text-danger" data-cy={`error-${name}`}>
                    {message}
                </small>
            )}
        </div>
    );
};

export default CreatableInput;

CreatableInput.propTypes = {
    control            : PropTypes.any.isRequired,
    name               : PropTypes.string.isRequired,
    id                 : PropTypes.string.isRequired,
    label              : PropTypes.string,
    className          : PropTypes.string,
    placeholder        : PropTypes.string,
    required           : PropTypes.bool,
    disabled           : PropTypes.bool,
    errMsg             : PropTypes.string,
    showErrMsg         : PropTypes.bool,
    tooltipContent     : PropTypes.string,
    errors             : PropTypes.object,
    options            : PropTypes.arrayOf(PropTypes.any),
    maxAllowedSelection: PropTypes.number,
    isMulti            : PropTypes.bool
};
